import { Link } from 'react-router-dom';

const ICON_PATHS: Record<'profile' | 'logout', string> = {
  profile: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 5v1a3 3 0 01-3 3H6a3 3 0 01-3-3V6a3 3 0 013-3h4a3 3 0 013 3v1',
};

type AccountIconButtonProps = {
  icon: 'profile' | 'logout';
  ariaLabel: string;
  className?: string;
} & (
  | { to: string; onClick?: never; ariaDisabled?: never }
  // Visible-but-inert state for an in-flight action (CLAUDE.md's aria-disabled convention — the
  // guarding condition itself lives in the caller's onClick handler, not here). Scoped to button
  // mode only — a Link has no in-flight state to guard.
  | { to?: never; onClick: () => void; ariaDisabled?: boolean }
);

/**
 * SF-138 Component 1: shared Profile/Logout icon-button primitive, extracted from `Layout.tsx`'s
 * SF-129 inline JSX. Icon + label + action only — positioning is entirely caller-supplied via
 * `className`, composed directly on the rendered `<Link>`/`<button>` (no wrapper).
 *
 * Drops `Layout.tsx`'s `hover:text-white` class — CLAUDE.md documents it as known-dead (these
 * buttons never set an inline `color` a hover pseudo-class could override).
 */
export function AccountIconButton({ icon, ariaLabel, className, ariaDisabled, to, onClick }: AccountIconButtonProps) {
  const sharedClassName = className ?? '';
  const sharedStyle = {
    backgroundColor: '#0b0e19',
    color: '#7b83a5',
    minWidth: 44,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const;

  const iconSvg = (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICON_PATHS[icon]} />
    </svg>
  );

  if (to !== undefined) {
    return (
      <Link to={to} className={sharedClassName} style={sharedStyle} aria-label={ariaLabel}>
        {iconSvg}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      className={sharedClassName}
      style={sharedStyle}
      aria-label={ariaLabel}
      aria-disabled={ariaDisabled}
    >
      {iconSvg}
    </button>
  );
}
